import type { ForecastOpportunitySummary, OpportunityScenarioView } from "./opportunities"

export interface SelectedOpportunityTotals {
  weighted_forecast_dollars: number
  full_forecast_dollars: number
}

/** Totals for opportunities included in the panel selection (empty selection = all open opportunities). */
export function summarize_selected_opportunity_forecast_totals(
  opportunities: ForecastOpportunitySummary[],
  selected_opportunity_ids: Set<string>
): SelectedOpportunityTotals {
  const rows =
    selected_opportunity_ids.size === 0
      ? opportunities
      : opportunities.filter((o: ForecastOpportunitySummary) => selected_opportunity_ids.has(o.id))
  return {
    weighted_forecast_dollars: rows.reduce(
      (sum: number, o: ForecastOpportunitySummary) => sum + o.weighted_revenue,
      0
    ),
    full_forecast_dollars: rows.reduce((sum: number, o: ForecastOpportunitySummary) => sum + o.full_revenue, 0),
  }
}

/** Amount of opportunity revenue folded into grid totals for the active scenario (baseline hides opportunity dollars). */
export function opportunity_amount_in_scenario_totals(
  scenario_view: OpportunityScenarioView,
  totals: SelectedOpportunityTotals
): number {
  if (scenario_view === "baseline") return 0
  if (scenario_view === "weighted") return totals.weighted_forecast_dollars
  return totals.full_forecast_dollars
}

export interface OpportunityKpiDetailLine {
  title: string
  /** Raw dollars; caller formats for display. */
  amount: number
}

/**
 * Lines to show under Remaining Forecast / Total Annual when open opportunities exist.
 * Keeps copy consistent and reusable across cards.
 */
export function build_opportunity_kpi_detail_lines(
  scenario_view: OpportunityScenarioView,
  totals: SelectedOpportunityTotals
): OpportunityKpiDetailLine[] {
  const lines: OpportunityKpiDetailLine[] = []
  const in_totals = opportunity_amount_in_scenario_totals(scenario_view, totals)
  const scenario_title =
    scenario_view === "baseline"
      ? "Opportunities (held out of totals)"
      : scenario_view === "weighted"
        ? "Opportunities (weighted)"
        : "Opportunities (full upside)"

  lines.push({
    title: scenario_title,
    amount: in_totals,
  })

  if (scenario_view === "baseline") {
    if (totals.full_forecast_dollars > totals.weighted_forecast_dollars) {
      lines.push({
        title: "Remaining potential upside",
        amount: totals.full_forecast_dollars - totals.weighted_forecast_dollars,
      })
    }
    lines.push({
      title: "Same opportunities at weighted likelihood",
      amount: totals.weighted_forecast_dollars,
    })
    return lines
  }

  if (scenario_view === "weighted" && totals.full_forecast_dollars > totals.weighted_forecast_dollars) {
    lines.push({
      title: "Remaining potential upside",
      amount: totals.full_forecast_dollars - totals.weighted_forecast_dollars,
    })
  }

  if (scenario_view === "full_upside") {
    if (totals.full_forecast_dollars > totals.weighted_forecast_dollars) {
      lines.push({
        title: "Remaining potential upside",
        amount: totals.full_forecast_dollars - totals.weighted_forecast_dollars,
      })
    }
    if (totals.weighted_forecast_dollars > 0) {
      lines.push({
        title: "At weighted likelihood (for comparison)",
        amount: totals.weighted_forecast_dollars,
      })
    }
  }

  return lines
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("summarize_selected_opportunity_forecast_totals", () => {
    const opp = (id: string, w: number, f: number): ForecastOpportunitySummary => ({
      id,
      name: "O",
      likelihood_pct: 50,
      status: "open",
      color: "#000",
      weighted_revenue: w,
      full_revenue: f,
      row_count: 1,
    })

    it("sums all opportunities when selection is empty", () => {
      const t = summarize_selected_opportunity_forecast_totals([opp("a", 100, 200), opp("b", 50, 80)], new Set())
      expect(t.weighted_forecast_dollars).toBe(150)
      expect(t.full_forecast_dollars).toBe(280)
    })

    it("filters to selected ids", () => {
      const t = summarize_selected_opportunity_forecast_totals([opp("a", 100, 200), opp("b", 50, 80)], new Set(["b"]))
      expect(t.weighted_forecast_dollars).toBe(50)
      expect(t.full_forecast_dollars).toBe(80)
    })
  })

  describe("opportunity_amount_in_scenario_totals", () => {
    const totals = { weighted_forecast_dollars: 100, full_forecast_dollars: 200 }

    it("returns 0 for baseline", () => {
      expect(opportunity_amount_in_scenario_totals("baseline", totals)).toBe(0)
    })

    it("returns weighted or full for other views", () => {
      expect(opportunity_amount_in_scenario_totals("weighted", totals)).toBe(100)
      expect(opportunity_amount_in_scenario_totals("full_upside", totals)).toBe(200)
    })
  })
}
